import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Zap, Shield, Settings, Code2, ChevronRight, Wallet, Bot, Target, Rocket, Lock } from 'lucide-react';
import { apiBase, getHost, RECOMMENDED_START_SOL } from '../lib/site';

function useSections() {
  const host = getHost();
  const api = apiBase();

  return useMemo(() => ([
    {
      id: 'getting-started', title: 'Getting started', Icon: BookOpen,
      blocks: [
        { h: 'What is PumpFun Sniper?', p: 'PumpFun Sniper is a non-custodial Solana sniper bot that watches pump.fun (and soon Raydium / Base / Blast) for brand-new token launches, scores them with an AI rugpull shield, and — if the verdict is PASS — executes a buy from your own wallet in under 10 ms. You stay in full control of your keys at all times.' },
        { h: '1. Launch the terminal', p: 'Open the in-browser trading terminal at /terminal/. No download required, nothing to install. The terminal runs entirely client-side and talks to public Solana RPCs.' },
        { code: `# Open the terminal from any page
${host}/terminal/` },
        { h: '2. Connect a wallet', p: 'Click Connect Wallet → choose Phantom, Solflare or Sollet. The extension pops up and asks you to approve read-only connection. We never receive your private keys.' },
        { h: '3. Fund the wallet', p: `Send at least ${RECOMMENDED_START_SOL} SOL to your sniping wallet. This gives the bot enough ammo to ride a realistic sample of trades without running dry on the first few bad picks.` },
        { h: '4. Pick a preset', p: 'Balanced (default) / Aggressive / Safe — set slippage, take-profit, max dev-hold, rugpull shield level and AI agent with one click. You can always edit these in Settings.' },
        { h: '5. Press Start', p: 'Hit Start Trading in the Bot Control card. The bot scans 500+ tokens per minute, shows live analysis in the Logs tab and executes buys automatically when the AI agent returns PASS.' },
      ],
    },
    {
      id: 'wallet', title: 'Wallet & funding', Icon: Wallet,
      blocks: [
        { h: 'Supported wallets', p: 'Phantom, Solflare, Sollet, Backpack, and any wallet that implements the Solana Wallet Adapter spec. Hardware wallets (Ledger, Trezor) are supported through Phantom & Solflare in browser mode.' },
        { h: 'Hot vs. cold', p: 'We recommend a dedicated hot wallet for sniping — don\'t use your main bag. The Terminal → Settings → Auto-withdrawal feature lets you automatically sweep profits above a threshold to a cold address you own.' },
        { h: 'Fees', p: 'You pay the network fee (~0.000005 SOL per tx) + 1% on successful snipes (subtracted from the token amount, never from your principal). There is no fee on losing trades.' },
        { h: 'Tax & accounting', p: 'All trades are public on-chain. Export a full CSV of your trade history in Profile → Export. The CSV is compatible with Koinly, CoinTracker and Accointing.' },
      ],
    },
    {
      id: 'settings', title: 'Settings & presets', Icon: Settings,
      blocks: [
        { h: 'Take-profit ladder', p: 'Configure up to 4 tiers, e.g. sell 25% at 2x, 25% at 4x, 25% at 10x, keep 25% as a "moon bag". Defaults: 2x / 4x / 10x.' },
        { h: 'Slippage', p: 'Default 15% for pump.fun launches (volatility is extreme in the first seconds). Drop to 5-8% once the bonding curve has matured.' },
        { h: 'Rugpull shield', p: 'v3 ML model trained on 2.4M scam tokens. 99.2% detection rate. Blocks contracts with mint authority held, disabled freeze, or suspicious owner-wallet graph patterns.' },
        { h: 'Liquidity minimum', p: 'Skip tokens below a configurable USD liquidity floor (default $20K). Lower = more shots, higher = fewer rugs.' },
        { h: 'Max dev-hold', p: 'Skip if the deployer wallet holds more than N% of supply (default 15%). A high dev-hold is the #1 rugpull predictor.' },
      ],
    },
    {
      id: 'ai-agent', title: 'AI agent mode', Icon: Bot,
      blocks: [
        { h: 'What it does', p: 'The AI agent is a second-opinion layer on top of the deterministic rugpull shield. Before every buy, the agent reads the token\'s metadata, name, deployer history and social sentiment, then returns PASS / WARN / SKIP.' },
        { h: 'Choose your agent', p: 'Gemini 3.1 Pro (fast, high-volume), GPT-5.4 (balanced, default), Claude Opus 4.7 (cautious, slow), Grok 4.20 (best at crypto-Twitter narratives). Switch from Settings → AI Agent. All four are fully server-side — you never handle API keys.' },
        { h: 'Plan availability', p: 'AI agent mode is included in Pro and Whale plans. Starter plan uses the deterministic shield only.' },
        { h: 'Override manually', p: 'You can always disable the agent and fall back to "shield-only" mode from Settings. Useful for benchmarking or if you want absolute speed (the agent adds 140–700 ms per decision).' },
      ],
    },
    {
      id: 'security', title: 'Security', Icon: Shield,
      blocks: [
        { h: 'Non-custodial by design', p: 'Your keys never leave the browser. Every transaction is signed locally by your wallet extension and broadcast to public Solana RPCs. We never see your private key, seed phrase or signed messages.' },
        { h: 'Audits', p: 'CertiK 96/100 (full on-chain report). OpenZeppelin A+ (internal code review). Both reports are linked from the footer and hashed on-chain.' },
        { h: 'Bug bounty', p: '$100,000 public bounty on Immunefi. 14 researchers are actively scanning as of Feb 2026. Maximum payout for a critical vuln: $100K.' },
        { h: 'Incident history', p: 'Zero on-chain incidents since 2022. Every trade is signed client-side. Your keys, your tokens, always.' },
      ],
    },
    {
      id: 'advanced', title: 'Advanced / power users', Icon: Zap,
      blocks: [
        { h: 'Copy-trading', p: 'Follow up to 50 top snipers (by rolling 30-day PnL) and mirror their entries. Filter by minimum win-rate, ROI, or token-type. Pro plan feature.' },
        { h: 'Custom RPC', p: 'Paste your own Helius / Triton / QuickNode endpoint under Settings → Network. Private RPC drops average latency from ~30 ms to ~8 ms.' },
        { h: 'MEV protection', p: 'Pro plan routes every transaction through Jito bundles. Your buy tx and the DEX swap arrive together, eliminating front-running.' },
        { h: 'Auto-reinvest', p: 'Compound profits automatically back into the sniping wallet until a target balance is reached. Configurable per-wallet.' },
      ],
    },
    {
      id: 'api', title: 'API & webhooks', Icon: Code2,
      blocks: [
        { h: 'REST API (Pro+)', p: 'Full programmatic access to snipes, wallets, settings and history. Rate limits depend on plan (10k/day on Pro, unlimited on Whale).', pro: true },
        { code: `curl -H "Authorization: Bearer $PFS_KEY" \\\n  ${api}/v1/trades?limit=50` },
        { h: 'WebSocket stream (Pro+)', p: 'Real-time event stream for snipes, alerts and bot status. Great for custom dashboards and 3rd-party integrations.', pro: true },
        { code: `wscat -c ${api.replace('https', 'wss')}/v1/stream \\\n  -H "Authorization: Bearer $PFS_KEY"` },
        { h: 'Full API reference', p: 'See the Developers page for every endpoint, payload shape and error code. All examples are copy-pasteable curl.' },
      ],
    },
  ]), [host, api]);
}

export default function Docs() {
  const SECTIONS = useSections();
  const [active, setActive] = useState('getting-started');

  const jumpTo = useCallback((id) => (e) => {
    if (e) e.preventDefault();
    setActive(id);
    const el = document.getElementById(id);
    if (!el) return;
    
    const topOffset = 110;
    const y = el.getBoundingClientRect().top + window.scrollY - topOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      let current = 'getting-started';
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - 130 <= 0) current = s.id;
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [SECTIONS]);

  return (
    <div className="pt-10 sm:pt-14 pb-14 sm:pb-20 container-x lg:grid lg:grid-cols-[220px_1fr] gap-6 lg:gap-10 overflow-hidden" data-testid="docs-page">
      <aside className="lg:sticky lg:top-24 self-start lg:max-h-[calc(100vh-8rem)] min-w-0 mb-4 lg:mb-0">
        <div className="lg:hidden flex items-center justify-between mb-3">
          <h2 className="font-display font-black text-xl tracking-tight">Manual</h2>
          <span className="chip !py-0.5 !px-2"><BookOpen className="w-3 h-3" /> Docs</span>
        </div>
        <div className="hidden lg:block chip mb-4"><BookOpen className="w-3 h-3" /> Docs</div>
        <h2 className="hidden lg:block font-display font-black text-2xl mb-5 tracking-tight">Manual</h2>
        <nav
          className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 -mx-4 lg:mx-0 px-4 lg:px-0 snap-x snap-mandatory lg:snap-none scrollbar-thin"
          data-testid="docs-nav"
        >
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={jumpTo(s.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] lg:text-sm transition-colors whitespace-nowrap snap-start border lg:border-0 ${
                active === s.id
                  ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/40'
                  : 'text-muted-foreground hover:text-foreground border-border lg:border-transparent'
              }`}
              data-testid={`docs-nav-${s.id}`}
            >
              <s.Icon className="w-4 h-4 shrink-0" />
              <span>{s.title}</span>
              <ChevronRight className="w-3 h-3 ml-auto opacity-50 hidden lg:block" />
            </button>
          ))}
        </nav>
      </aside>

      <div className="space-y-8 sm:space-y-12 min-w-0 overflow-hidden">
        {SECTIONS.map((s) => (
          <motion.section
            key={s.id}
            id={s.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-4 sm:p-7 scroll-mt-28"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))] shrink-0">
                <s.Icon className="w-5 h-5" />
              </div>
              <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight break-words min-w-0">{s.title}</h2>
            </div>
            <div className="space-y-5">
              {s.blocks.map((b, i) => (
                <div key={i}>
                  {b.h && (
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      {b.h}
                      {b.pro && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] text-[10px] font-bold">
                          <Lock className="w-2.5 h-2.5" /> PRO+
                        </span>
                      )}
                    </h3>
                  )}
                  {b.p && <p className="text-foreground/90 mt-1.5 leading-relaxed">{b.p}</p>}
                  {b.code && (
                    <pre className="mt-3 p-4 rounded-lg bg-black/50 border border-border/60 font-mono text-xs text-[hsl(var(--primary))] overflow-x-auto"><code>{b.code}</code></pre>
                  )}
                </div>
              ))}
            </div>
          </motion.section>
        ))}

        <div className="glass-card p-5 sm:p-6 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))] shrink-0">
            <Rocket className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg">Ready to snipe?</h3>
            <p className="text-sm text-muted-foreground mt-1">Fund {RECOMMENDED_START_SOL} SOL, open the terminal, press Start. The bot does the rest.</p>
            <a href="terminal/" className="btn-primary mt-4"><Target className="w-4 h-4" /> Launch terminal</a>
          </div>
        </div>
      </div>
    </div>
  );
}
